import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { getUrl, uploadData, remove } from "aws-amplify/storage";
import AuthInterceptor from "../../interceptors/authInterceptor.jsx";
import Layout from "../../components/layout/AdminLayout.jsx";

const client = generateClient();

const Galeria = () => {
  const [imageFile, setImageFile] = useState(null);
  const [etiquetas, setEtiquetas] = useState("");
  const [isPublica, setIsPublica] = useState(true);
  const [costo, setCosto] = useState(0);
  const [imagenes, setImagenes] = useState([]);
  const [editingEtiquetas, setEditingEtiquetas] = useState("");

  useEffect(() => {
    fetchImagenes();
  }, []);

  const resetForm = () => {
    setImageFile(null);
    setEtiquetas("");
    setIsPublica(true);
    setCosto(0);
  };

  const fetchImagenes = async () => {
    try {
      const { data } = await client.models.Imagen.list({
        authMode: "userPool",
      });
      const imagesWithUrls = await Promise.all(
        data.map(async (imagen) => {
          const { url } = await getUrl({ path: imagen.filePath });
          return { ...imagen, previewUrl: url.href };
        }),
      );
      setImagenes(imagesWithUrls);
    } catch (error) {
      console.error("Error al obtener imágenes:", error);
    }
  };

  const handleFileChange = (e) => setImageFile(e.target.files[0]);
  const handleEtiquetasChange = (e) => setEtiquetas(e.target.value);
  const handleIsPublicaChange = (e) => {
    setIsPublica(e.target.checked);
    if (e.target.checked) setCosto(0);
  };
  const handleCostoChange = (e) => setCosto(parseFloat(e.target.value) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      alert("Por favor selecciona un archivo.");
      return;
    }

    try {
      const filePath = `galeria/${isPublica ? "public" : "private"}/${imageFile.name}`;
      await uploadData({ path: filePath, data: imageFile }).result;

      const imagenResult = await client.models.Imagen.create(
        {
          filePath: filePath,
          etiquetas: etiquetas,
          isPublica: isPublica,
          costo: costo,
        },
        {
          authMode: "userPool",
        },
      );

      resetForm();
      fetchImagenes();
    } catch (error) {
      console.error("Error al subir la imagen:", error);
      alert("Hubo un error al subir la imagen.");
    }
  };

  const handleDelete = async (id, filePath) => {
    if (!window.confirm("¿Seguro que deseas eliminar esta imagen?")) return;

    try {
      await remove({ path: filePath });
      await client.models.Imagen.delete(
        {
          id,
        },
        {
          authMode: "userPool",
        },
      );
      fetchImagenes();
    } catch (error) {
      console.error("Error al eliminar la imagen:", error);
    }
  };

  const handleEditEtiquetas = (id, value) => {
    setEditingEtiquetas((prev) => ({ ...prev, [id]: value }));
  };

  const handleUpdateEtiquetas = async (id) => {
    const etiquetasActualizadas = editingEtiquetas[id];
    try {
      await client.models.Imagen.update(
        {
          id,
          etiquetas: etiquetasActualizadas,
        },
        {
          authMode: "userPool",
        },
      );
      setEditingEtiquetas((prev) => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });
      fetchImagenes();
    } catch (error) {
      console.error("Error al actualizar etiquetas:", error);
      alert("No se pudieron actualizar las etiquetas.");
    }
  };

  return (
    <Layout>
      <AuthInterceptor>
        <div className="min-h-screen bg-gray-100 p-6">
          <div className="mx-auto max-w-3xl rounded-lg bg-white p-6 shadow-md">
            <h2 className="mb-6 text-center text-2xl font-bold text-gray-800">
              Subir Nueva Imagen
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Archivo
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  required
                  className="w-full rounded border p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Etiquetas
                </label>
                <input
                  type="text"
                  value={etiquetas}
                  onChange={handleEtiquetasChange}
                  className="w-full rounded border p-2"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={isPublica}
                  onChange={handleIsPublicaChange}
                  className="h-4 w-4"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Pública
                </label>
              </div>
              {!isPublica && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Costo
                  </label>
                  <input
                    type="number"
                    value={costo}
                    onChange={handleCostoChange}
                    className="w-full rounded border p-2"
                  />
                </div>
              )}
              <button
                type="submit"
                className="w-full rounded bg-indigo-600 py-2 text-white hover:bg-indigo-700"
              >
                Subir Imagen
              </button>
            </form>
          </div>

          {/* Grid de imágenes */}
          <div className="mt-10">
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Galería</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {imagenes.map((imagen) => (
                <div
                  key={imagen.id}
                  className="rounded-lg bg-white p-4 shadow-md"
                >
                  <img
                    src={imagen.previewUrl}
                    alt="Imagen"
                    className="h-40 w-full rounded-md object-cover"
                  />
                  <div className="mt-2">
                    <label className="text-sm font-medium text-gray-700">
                      Etiquetas
                    </label>
                    <input
                      type="text"
                      value={editingEtiquetas[imagen.id] ?? imagen.etiquetas}
                      onChange={(e) =>
                        handleEditEtiquetas(imagen.id, e.target.value)
                      }
                      className="w-full rounded border p-1"
                    />
                    <button
                      onClick={() => handleUpdateEtiquetas(imagen.id)}
                      className="mt-2 w-full rounded bg-blue-500 py-1 text-white hover:bg-blue-600"
                    >
                      Guardar
                    </button>
                  </div>
                  {/* <div className="mt-2 text-gray-600">
                    <p><strong>Costo:</strong> {imagen.isPublica ? "Gratis" : `$${imagen.costo}`}</p>
                    <p><strong>Tipo:</strong> {imagen.isPublica ? "Pública" : "Privada"}</p>
                  </div> */}
                  <button
                    onClick={() => handleDelete(imagen.id, imagen.filePath)}
                    className="mt-3 w-full rounded bg-red-500 py-1 text-white hover:bg-red-600"
                  >
                    Eliminar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AuthInterceptor>
    </Layout>
  );
};

export default Galeria;
